import { v4 as uuidV4 } from 'uuid';

import {
  type FilterLegacy,
  SubjectVariableCriteriaSelectionTypeLegacy,
  type SubjectVariableLegacy,
  SubjectVariableTypeLegacy,
} from '@verantos-dev/types-cohorts';
import { Cohort_definitionContext } from '@verantos-dev/util-cohort-builder-typescript-runtime';

import getActualValueFromDSL from '../helpers/getActualValueFromDSL';

export const generateSubjectVariablesFromDSL = (
  cohortDefinitionTree: Cohort_definitionContext,
  dirty?: boolean // set to true to annotate input fields as dirty
): SubjectVariableLegacy[] | [] => {
  if (!cohortDefinitionTree.subjects_variable(0)) return [];

  const subjectsVariables = cohortDefinitionTree.subjects_variable();
  // fetching inclusion and exclusion criteria sets
  let exclusionCriteriaSet: string[] = [];
  let inclusionCriteriaSet: string[] = [];

  try {
    exclusionCriteriaSet =
      (cohortDefinitionTree?.exclusion_criteria &&
        cohortDefinitionTree?.exclusion_criteria()?.getChildCount() !== 0 &&
        cohortDefinitionTree
          ?.exclusion_criteria()
          ?.cohort_criterion_set()
          ?.SUBJECTS_VARIABLE_NAME()
          ?.map((subjectVariable) => {
            return subjectVariable?.getText();
          })) ||
      [];
  } catch (error) {
    console.error('Error fetching exclusion criteria set', error);
    exclusionCriteriaSet = [];
  }

  try {
    inclusionCriteriaSet =
      (cohortDefinitionTree?.inclusion_criteria &&
        cohortDefinitionTree?.inclusion_criteria()?.getChildCount() !== 0 &&
        cohortDefinitionTree
          ?.inclusion_criteria()
          ?.cohort_criterion_set()
          ?.SUBJECTS_VARIABLE_NAME()
          ?.map((subjectVariable) => {
            return subjectVariable?.getText();
          })) ||
      [];
  } catch (error) {
    console.error('Error fetching inclusion criteria set', error);
    inclusionCriteriaSet = [];
  }

  try {
    return subjectsVariables.map((subjectVariable) => {
      // Check if the subject variable is part of the inclusion or exclusion criteria
      let criteriaSelectionValue = SubjectVariableCriteriaSelectionTypeLegacy.NOT_APPLIED;
      const subjectVariableName = subjectVariable.SUBJECTS_VARIABLE_NAME().getText();

      if (inclusionCriteriaSet.length > 0 || exclusionCriteriaSet.length > 0) {
        const isInclusionCriteria = inclusionCriteriaSet.some(
          (item) => item.toLowerCase() === subjectVariableName.toLowerCase()
        );
        const isExclusionCriteria = exclusionCriteriaSet.some(
          (item) => item.toLowerCase() === subjectVariableName.toLowerCase()
        );

        if (isInclusionCriteria) {
          // TODO: read from DSL - need an update to the DSL
          criteriaSelectionValue = SubjectVariableCriteriaSelectionTypeLegacy.INCLUSION;
        } else if (isExclusionCriteria) {
          // TODO: read from DSL - need an update to the DSL
          criteriaSelectionValue = SubjectVariableCriteriaSelectionTypeLegacy.EXCLUSION;
        }
      }

      if (subjectVariable.demographic_event_criteria()) {
        const demographicEventFilters = subjectVariable
          .demographic_event_criteria()
          ?.demographic_event_filters()
          ?.demographic_event_filter();
        const conceptFilters: FilterLegacy[] = [];
        const numericFilters: FilterLegacy[] = [];
        // const conceptFilters: FilterLegacy[] =
        demographicEventFilters?.map((demographicEventFilter) => {
          const conceptGroupFilter = demographicEventFilter
            .demographic_concept_filter()
            ?.demographic_concept_group_filter();
          const numericFilter = demographicEventFilter
            .demographic_numeric_filter()
            ?.demographic_int_filter();
          if (conceptGroupFilter)
            conceptFilters.push({
              id: uuidV4(),
              inputFields: conceptGroupFilter
                ? [
                    {
                      id: uuidV4(),
                      value: conceptGroupFilter.PERSON_CONCEPT_ATTRIBUTE().getText() || '',
                      dirty,
                    },

                    {
                      id: uuidV4(),
                      value: conceptGroupFilter.IN()?.getText() || '',
                      dirty,
                    },
                    {
                      id: uuidV4(),
                      value:
                        conceptGroupFilter?.concept_group_selection().CONCEPT_GROUP().toString() ||
                        '',
                      dirty,
                    },
                  ]
                : [],
            });
          if (numericFilter) {
            numericFilters.push({
              id: uuidV4(),
              inputFields: numericFilter
                ? [
                    {
                      id: uuidV4(),
                      value: numericFilter.PERSON_INT_ATTRIBUTE().getText() || '',
                      dirty,
                    },
                    {
                      id: uuidV4(),
                      value: numericFilter.comparison_op().getText() || '',
                      dirty,
                    },
                    {
                      id: uuidV4(),
                      value: numericFilter.INT().getText() || '',
                      dirty,
                    },
                  ]
                : [],
            });
          }
        });

        return {
          id: uuidV4(),
          name: getActualValueFromDSL(subjectVariableName) || '',
          type: SubjectVariableTypeLegacy.DEMOGRAPHICS,
          conceptFilters,
          numericFilters,
          countFilter: undefined,
          criteriaSelection: criteriaSelectionValue,
        };
      }

      if (subjectVariable.event_count_criteria()) {
        const subjectCountCriteria = subjectVariable.event_count_criteria()?.count_criteria();
        const countFilter = {
          id: uuidV4(),
          inputFields: subjectCountCriteria
            ? [
                {
                  id: uuidV4(),
                  value:
                    subjectCountCriteria
                      .subject_variable_event_name()
                      .getText()
                      .replace('SUBJECT_VARIABLE_', '') || '',
                  dirty,
                },
                {
                  id: uuidV4(),
                  value: subjectCountCriteria.comparison_op().getText() || '',
                  dirty,
                },
                {
                  id: uuidV4(),
                  value: subjectCountCriteria.INT().getText() || '',
                  dirty,
                },
              ]
            : [],
        };

        return {
          id: uuidV4(),
          name: getActualValueFromDSL(subjectVariableName),
          type: SubjectVariableTypeLegacy.COUNT,
          conceptFilters: [],
          numericFilters: [],
          countFilter,
          criteriaSelection: criteriaSelectionValue,
        };
      }

      return {
        id: uuidV4(),
        name:
          getActualValueFromDSL(
            subjectVariable?.SUBJECTS_VARIABLE_NAME &&
              subjectVariable?.SUBJECTS_VARIABLE_NAME()?.getText()
          ) || '',
        type: undefined,
        conceptFilters: [],
        numericFilters: [],
        countFilter: undefined,
        criteriaSelection: SubjectVariableCriteriaSelectionTypeLegacy.NOT_APPLIED,
      };
    });
  } catch (error) {
    return [];
  }
};
